import { useState, useEffect, useMemo } from 'react';
import { Hotel, TravellerInfo } from '../types/Quotation';
import { fetchHotels, HotelsPagination } from '../api/Hotels';
import { HotelSearchRoom } from '../components/PackageBuilder/roomOccupancy';

interface UseHotelsResult {
  hotels: Hotel[];
  loading: boolean;
  error?: Error;
  pagination?: HotelsPagination;
  priceJobId?: string;
}

export function useHotels(
  city: string,
  checkIn: string,
  checkOut: string,
  travellers: TravellerInfo,
  page: number,
  limit: number,
  filters: { StarRating?: number; MealType?: string; HotelName?: string; Amenities?: string[]; Provider?: string },
  trigger: number,
  childrenAges?: number[],
  roomsOverride?: HotelSearchRoom[]
): UseHotelsResult {
  const [hotels, setHotels] = useState<Hotel[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error>();
  const [pagination, setPagination] = useState<HotelsPagination | undefined>();
  const [priceJobId, setPriceJobId] = useState<string | undefined>();
  const roomsKey = useMemo(() => JSON.stringify(Array.isArray(roomsOverride) ? roomsOverride : []), [roomsOverride]);

  // const filtersKey = JSON.stringify(filters);

  useEffect(() => {
    // Only search after user clicks the Search button (trigger > 0)
    if (!trigger || trigger <= 0) {
      setLoading(false);
      setError(undefined);
      setPagination(undefined);
      setHotels([]);
      setPriceJobId(undefined);
      return;
    }
    const code = (city || '').trim();
    const hasValidCity = /^(?:[A-Za-z]{3}|\d+)$/.test(code);
    const ci = (checkIn || '').trim();
    const co = (checkOut || '').trim();
    const ciDate = ci ? new Date(ci) : null;
    const coDate = co ? new Date(co) : null;
    const hasValidDates = !!(ciDate && coDate && !isNaN(ciDate.getTime()) && !isNaN(coDate.getTime()) && ciDate < coDate);

    if (!hasValidCity || !hasValidDates) {
      // Do not call API until we have required inputs
      setLoading(false);
      setError(undefined);
      setPagination(undefined);
      setHotels([]);
      setPriceJobId(undefined);
      return;
    }

    setLoading(true);
    setError(undefined);

    const ages: number[] = Array.isArray(childrenAges)
      ? childrenAges.slice(0, Math.max(0, travellers.children || 0))
      : [];
    const rooms = Array.isArray(roomsOverride) && roomsOverride.length > 0
      ? roomsOverride
      : [{
        Adults: travellers.adults,
        Children: travellers.children,
        ChildrenAges: ages,
      }];

    fetchHotels({
      city: code,
      checkIn: ci,
      checkOut: co,
      rooms,
      filters,
      page,
      limit,
    })
      .then(response => {
        setHotels((response && response.items) || []);
        setPagination(response?.pagination);
        setPriceJobId(
          (response as any)?.priceJobId
            || (response as any)?.price_job_id
            || (response as any)?.searchId
            || (response as any)?.search_id
            || (response as any)?.jobId
            || (response as any)?.job_id
            || (response as any)?.id
            || (response as any)?.search?.id
        );
      })
      .catch(err => {
        setError(err);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [trigger, page, limit, city, checkIn, checkOut, travellers.adults, travellers.children, childrenAges, filters, roomsKey, roomsOverride]);

  return { hotels, loading, error, pagination, priceJobId };
}
